import { createApi } from '@reduxjs/toolkit/query/react';
import {
  DexCopyBulkParams,
  DexCopyParams,
  DexCopyResult,
  DexFieldMap,
  DexPreviewParams,
  DexPreviewResult,
  DexReadinessParams,
  DexReadinessResult,
} from '~dex/types';

import { apiDexBase } from '~constants/api';

import { rtkBaseQueryWrapper, unwrap } from '~libs/axiosWrapper';

export const dexApi = createApi({
  reducerPath: 'dexApi',
  baseQuery: rtkBaseQueryWrapper({ baseUrl: apiDexBase }),
  tagTypes: ['DexReadiness'],
  endpoints: (builder) => ({
    getDexPreview: builder.query<DexPreviewResult, DexPreviewParams>({
      query: (params) => ({ url: '/export/preview', method: 'GET', params }),
      transformResponse: (payload) => unwrap<DexPreviewResult>(payload),
    }),
    getDexReadiness: builder.query<DexReadinessResult, DexReadinessParams>({
      query: (params) => ({ url: '/readiness', method: 'GET', params }),
      transformResponse: (payload) => unwrap<DexReadinessResult>(payload),
      providesTags: ['DexReadiness'],
    }),
    // TAV-3133: invalidating the DexReadiness tag automatically refetches the currently-mounted
    // lazy readiness query (DexReadinessPage), so a successful copy is reflected without any
    // manual refetch wiring.
    // resolves a service type's DEX field mapping with no appointment in existence, so
    // the DEX tab can render on an unsaved appointment. Deliberately not tagged - a service type's
    // mapping is reference data, unaffected by any DEX save.
    getDexFieldMap: builder.query<DexFieldMap | null, { service_type_id: string | number }>({
      query: (params) => ({ url: '/field-map', method: 'GET', params }),
      transformResponse: (payload) => unwrap<DexFieldMap | null>(payload),
    }),
    copyDexData: builder.mutation<DexCopyResult, DexCopyParams>({
      query: (body) => ({ url: '/copy', method: 'POST', body }),
      transformResponse: (payload) => unwrap<DexCopyResult>(payload),
      invalidatesTags: ['DexReadiness'],
    }),
    copyDexDataBulk: builder.mutation<DexCopyResult, DexCopyBulkParams>({
      query: (body) => ({ url: '/copy-bulk', method: 'POST', body }),
      transformResponse: (payload) => unwrap<DexCopyResult>(payload),
      invalidatesTags: ['DexReadiness'],
    }),
  }),
});

export const {
  useGetDexFieldMapQuery,
  useLazyGetDexPreviewQuery,
  useLazyGetDexReadinessQuery,
  useCopyDexDataMutation,
  useCopyDexDataBulkMutation,
} = dexApi;
